// AuthCallbackHandler — F8.7b · listens for query param callbacks and reacts.
//
// URL params handled:
//   ?login=success        → toast "Welcome back!" + refresh() + trigger intent
//   ?login=oauth_failed   → toast error + open login modal
//   ?login=state_mismatch → toast error + open login modal
//   ?login=state_expired  → toast error + open login modal
//   ?login=no_email       → toast error + open login modal
//   ?verify=success       → toast "Email verified" + open login modal
//   ?verify=invalid       → toast error + open login modal
//   ?verify=expired       → toast error + open login modal
//   ?logout=success       → toast "Logged out"
//
// After handling, strips the params from URL via history.replaceState so
// refreshes don't retrigger.
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useAuthModal } from "@/context/AuthModalContext";
import { getAuthIntent, clearAuthIntent } from "@/lib/authIntent";
import { api } from "@/lib/api";

export default function AuthCallbackHandler() {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation("landing_v2");
    const { refresh } = useAuth();
    const { openModal } = useAuthModal();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const login = params.get("login");
        const verify = params.get("verify");
        const logout = params.get("logout");

        if (!login && !verify && !logout) return;

        (async () => {
            if (login === "success") {
                try { window.localStorage.setItem("glms_session", "1"); } catch { /* noop */ }
                await refresh();
                toast.success(t("toasts.logged_in"));
                // Fire intent (if any) after login.
                const intent = getAuthIntent();
                if (intent?.action === "checkout" && intent.plan) {
                    clearAuthIntent();
                    try {
                        const co = await api.subscriptionCheckout(intent.plan, intent.interval || "monthly");
                        if (co?.url) { window.location.href = co.url; return; }
                    } catch { /* silent — user can retry manually */ }
                }
                // Phase I Wave 2.5 · Default post-login target is /v2/dashboard (V12 shell).
                // Backend may still redirect Google OAuth callback to /dashboard?login=success,
                // but the root App redirect converts that to /v2/dashboard.
                if (location.pathname === "/" || location.pathname === "/v2") {
                    navigate("/app/dashboard", { replace: true });
                    return;
                }
            } else if (login && ["oauth_failed", "state_mismatch", "state_expired", "no_email"].includes(login)) {
                toast.error(t("auth.modal.error_generic"));
                openModal("login");
            } else if (verify === "success") {
                toast.success(t("toasts.verified"));
                openModal("login");
            } else if (verify === "invalid" || verify === "expired") {
                toast.error(t("auth.modal.error_generic"));
                openModal("login");
            } else if (logout === "success") {
                toast(t("toasts.logged_out"));
            }
            // Strip params so a refresh doesn't retrigger.
            const clean = new URLSearchParams(location.search);
            ["login", "verify", "logout"].forEach((k) => clean.delete(k));
            const qs = clean.toString();
            navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" }, { replace: true });
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    return null;
}
